'use client';

import Link from 'next/link';
import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import {
  forgotPasswordSchema,
  type ForgotPasswordSchema,
} from '@/features/auth/schemas';
import { MainButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify, zodErrorsToFieldErrors } from '@web/utils';

import { useForgotPasswordMutation } from '../api/authApi';

import styles from './AuthForm.module.scss';

type ForgotPasswordFormProps = {
  inviteCode?: string;
  invitedEmail?: string;
};

function buildInviteLoginRoute(inviteCode: string, invitedEmail: string) {
  if (!inviteCode) {
    return appRoutes.auth.login;
  }

  const params = new URLSearchParams({ invite_code: inviteCode });
  if (invitedEmail) {
    params.set('email', invitedEmail);
  }

  return `${appRoutes.auth.login}?${params.toString()}`;
}

export function ForgotPasswordForm({
  inviteCode = '',
  invitedEmail = '',
}: ForgotPasswordFormProps) {
  const [forgotPassword, forgotStatus] = useForgotPasswordMutation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const trimmedInviteCode = inviteCode.trim();
  const trimmedInvitedEmail = invitedEmail.trim();

  const [formData, setFormData] = useState<ForgotPasswordSchema>({
    email: trimmedInvitedEmail,
    invite_code: trimmedInviteCode || undefined,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof ForgotPasswordSchema, string>>
  >({});

  const isBusy = forgotStatus.isLoading;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const parsed = forgotPasswordSchema.safeParse(formData);
    if (!parsed.success) {
      setErrors(zodErrorsToFieldErrors<ForgotPasswordSchema>(parsed.error));
      return;
    }

    try {
      const response = await forgotPassword({
        ...parsed.data,
        ...(trimmedInviteCode ? { invite_code: trimmedInviteCode } : {}),
      }).unwrap();

      setIsSubmitted(true);

      notify({
        type: 'success',
        message:
          (response?.message as string) ??
          'If the email exists, a reset code has been sent.',
      });
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form
      className={`${styles.form} ${styles.formActive}`}
      onSubmit={handleSubmit}
    >
      {isSubmitted ? (
        <div className={styles.checkEmail}>
          <h2>Check your email</h2>
          <p>We sent a reset link to {formData.email}.</p>
        </div>
      ) : (
        <SmartInput
          type="text"
          name="email"
          label={trimmedInvitedEmail ? 'Email from invite' : 'Email'}
          value={formData.email}
          onChange={(value) =>
            setFormData((prev) => ({
              ...prev,
              email: trimmedInvitedEmail ? prev.email : value,
            }))
          }
          schema={forgotPasswordSchema.shape.email}
          error={errors.email}
          readOnly={Boolean(trimmedInvitedEmail)}
        />
      )}

      <div className={styles.formActions}>
        <MainButton
          type="submit"
          disabled={isBusy}
          isLoading={isBusy}
          ariaLabel={isSubmitted ? 'Resend link' : 'Send reset link'}
        >
          {isSubmitted ? 'Resend link' : 'Send reset link'}
        </MainButton>

        <Link
          href={buildInviteLoginRoute(trimmedInviteCode, trimmedInvitedEmail)}
          className="inlineLink"
        >
          Back to log in
        </Link>
      </div>
    </form>
  );
}
